import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MDBRow, MDBCol } from "mdbreact";

import "./C4DistanceTravelled.scss"

import { Button, Label, TextArea } from "abs-ui-components";

import { AUTOSAVE_SUCCESS_MESSAGE, c41Fields, c42Fields } from "../../Constants";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import AutoSave from "../../../../../common/AutoSave/AutoSave";

const C4DistanceTravelled = forwardRef(({ c41DistanceTravelled, c42DistanceTravelledInIce, vesselMstId, refetchMonitoringData }, ref) => {
    const { control, getValues, reset, formState } = useFormContext();

    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);
    const { dirtyFields } = formState;

    const isC41Dirty = !!(dirtyFields?.c41 && Object.keys(dirtyFields.c41).length > 0);
    const isC42Dirty = !!(dirtyFields?.c42 && Object.keys(dirtyFields.c42).length > 0);


    useEffect(() => {
        if (c41DistanceTravelled) {
            reset(prev => ({ ...prev, c41: c41DistanceTravelled }));
        }
        if (c42DistanceTravelledInIce) {
            reset(prev => ({ ...prev, c42: c42DistanceTravelledInIce }));
        }
    }, [c41DistanceTravelled, c42DistanceTravelledInIce, reset]);

    AutoSave(ref, [], async () => {
        if (isC41Dirty) await handleSave41(true);
        if (isC42Dirty) await handleSave42(true);
    });

    const handleSave41 = async (isAuto = false) => {
        const formValues = getValues("c41");
        const payload = {
            ...c41Fields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const C4DistanceTravelledC41 = await callApi({
                method: "POST",
                url: API_ROUTES.C41(vesselMstId),
                data: payload,
            });
            if (C4DistanceTravelledC41?.isSuccess) {
                emitToasterMsg("success", "Table C.4.1", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c41: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.4.1", C4DistanceTravelledC41?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.4.1", error?.errorMessage);
        }
    };

    const handleSave42 = async (isAuto = false) => {
        const formValues = getValues("c42");
        const payload = {
            ...c42AdjustedFields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const C4DistanceTravelledC42 = await callApi({
                method: "POST",
                url: API_ROUTES.C42(vesselMstId),
                data: payload,
            });
            if (C4DistanceTravelledC42?.isSuccess) {
                emitToasterMsg("success", "Table C.4.2", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c42: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.4.2", C4DistanceTravelledC42?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.4.2", error?.errorMessage);
        }
    };

    const c42AdjustedFields = useMemo(() => {
        return c42Fields.map(f =>
            f.name === "dataSources"
                ? { ...f, label: "Formulae and data sources", name: "formulaeAndDataSources" }
                : f
        );
    }, []);

    return (
        <>
            <div className="c41-section-form mt-4">
                <MDBRow className="mb-2">
                    <MDBCol md="4">
                        <h6 className="titles-font">Title of procedure</h6>
                    </MDBCol>
                    <MDBCol md="6">
                        <h6 className="titles-font">
                            Recording and determining the distance per voyage made</h6>
                    </MDBCol>
                </MDBRow>

                {c41Fields.map(({ label, name, maxLength }, index) => (
                    <MDBRow className="align-items-center mb-3" key={index}>
                        <MDBCol md="4">
                            <Label name={label} bold />
                        </MDBCol>
                        <MDBCol md="4">
                            <Controller
                                name={`c41.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`c41_${name}`}
                                        className="textarea-resize"
                                        value={field?.value || ""}
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                    />
                                )}
                            />
                        </MDBCol>
                    </MDBRow>
                ))}
                <div className="text-center">
                    <Button id="c41_save_btn"
                        disabled={!isC41Dirty}
                        onClick={async () => {
                            manualSaveTriggered.current = true;
                            await handleSave41(false);
                        }}
                    >Save</Button>
                </div>
            </div>

            <div className="mp-headers">
                C4.2 Procedures for determining and recording the distance travelled when navigating through ice (voluntary monitoring):
            </div>

            <div className="c42-section-form mt-5">
                <MDBRow className="mb-2">
                    <MDBCol md="4">
                        <h6 className="titles-font">Title of procedure</h6>
                    </MDBCol>
                    <MDBCol md="6">
                        <h6 className="titles-font">
                            Determining and recording the distance travelled when navigating through ice</h6>
                    </MDBCol>
                </MDBRow>

                {c42AdjustedFields.map(({ label, name, maxLength }, index) => (
                    <MDBRow className="align-items-center mb-3" key={index}>
                        <MDBCol md="4">
                            <Label name={label} bold />
                        </MDBCol>
                        <MDBCol md="4">
                            <Controller
                                name={`c42.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`c42_${name}`}
                                        className="textarea-resize"
                                        value={field?.value || ""}
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                    />
                                )}
                            />
                        </MDBCol>
                    </MDBRow>
                ))}
                <div className="text-center">
                    <Button id="c42_save_btn"
                        disabled={!isC42Dirty}
                        onClick={async () => {
                            manualSaveTriggered.current = true;
                            await handleSave42(false);
                        }}
                    >Save</Button>
                </div>
            </div>
        </>
    );
});
export default C4DistanceTravelled;